import { Button } from 'components/ui/button';
import useGetRecentDocuments from 'domain/documents/hooks/useGetRecentDocuments';
import { X } from 'lucide-react';
import { useMemo } from 'react';
import RecentDocumentDropDownItem from './RecentDocumentDropDownItem';
import SearchSuggestionDropDownItem from './SearchSuggestionDropDownItem';
import { SearchDocument, indexedItemKey } from 'domain/documents/schemas/document';
import { useTranslation } from 'react-i18next';

interface RecentDocumentHistoryDropDownProps {
  type?: 'favorite' | 'recent';
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;

  query: string;
  minSearchLength?: number;

  suggestions?: SearchDocument[];
  isSuggestionsLoading?: boolean;
  onSelectSuggestion?: (suggestion: SearchDocument) => void;
}

export default function RecentDocumentHistoryDropDown({
  type = 'recent',
  isOpen,
  setIsOpen,
  query,
  minSearchLength = 3,
  suggestions = [],
  isSuggestionsLoading = false,
  onSelectSuggestion,
}: RecentDocumentHistoryDropDownProps) {
  const { t } = useTranslation('searchPage');

  const trimmedQuery = query.trim();
  const isSuggestionMode = trimmedQuery.length >= minSearchLength;

  const { data: recentDocuments, isLoading: isRecentLoading, isError: isRecentError } = useGetRecentDocuments();

  const recentToShow = useMemo(() => recentDocuments ?? [], [recentDocuments]);
  const suggestionsToShow = useMemo(() => suggestions, [suggestions]);
  // Ouvert, l'encadré dit toujours ce qu'il sait : chargement, liste, liste vide ou erreur. Le cacher
  // quand la liste est vide confondait « rien à montrer » et « lecture échouée », et une dérive de
  // contrat du service est restée invisible pendant des jours.
  const canShowDropdown = isOpen;

  const title = isSuggestionMode
    ? t('suggestion.suggestionTitle')
    : t(`suggestion.${type === 'favorite' ? 'favoriteTitle' : 'recentTitle'}`);
  const ariaLabel = isSuggestionMode
    ? t('suggestion.suggestionTitle')
    : t(`suggestion.${type === 'favorite' ? 'favoriteTitle' : 'recentTitle'}`);

  return (
    <div
      className={[
        'absolute right-0 left-0 z-20 mt-3 overflow-hidden rounded-2xl border-none shadow-lg',
        'border-2 transition-all duration-200 ease-out',
        canShowDropdown
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none -translate-y-2 opacity-0',
      ].join(' ')}
      role="listbox"
      aria-label={ariaLabel}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between bg-secondaryYellow px-4 py-3 text-white">
        <p className="text-md font-medium">{title}</p>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full transition hover:bg-white active:scale-95"
          onClick={() => setIsOpen(false)}
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="bg-white">
        <div className="max-h-80 overflow-auto p-2">
          {isSuggestionMode && (
            <>
              {isSuggestionsLoading && (
                <div className="space-y-2 p-2">
                  {Array.from({ length: 5 }).map((label, index) => (
                    <div
                      key={index}
                      className="h-12 animate-pulse rounded-xl border border-gray-100 bg-linear-to-r from-gray-50 via-gray-100 to-gray-50"
                    />
                  ))}
                </div>
              )}

              {!isSuggestionsLoading && suggestionsToShow.length === 0 && (
                <div className="p-6 text-center">
                  <p className="text-sm font-medium text-gray-900">{t('suggestion.noSuggestion')}</p>
                  <p className="mt-1 text-xs text-gray-500">{t('suggestion.tryAnotherKeyword')}</p>
                </div>
              )}

              {!isSuggestionsLoading && suggestionsToShow.length > 0 && (
                <div className="space-y-2">
                  {suggestionsToShow.map((suggestion, index) => (
                    <SearchSuggestionDropDownItem
                      key={suggestion.publicId}
                      suggestion={suggestion}
                      index={index}
                      canAnimateIn={canShowDropdown}
                      onClose={() => setIsOpen(false)}
                      onSelect={selected => onSelectSuggestion?.(selected)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {!isSuggestionMode && (
            <>
              {isRecentLoading && (
                <div className="space-y-2 p-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={index}
                      className="h-12 animate-pulse rounded-xl border border-gray-100 bg-linear-to-r from-gray-50 via-gray-100 to-gray-50"
                    />
                  ))}
                </div>
              )}

              {!isRecentLoading && isRecentError && (
                <div className="p-6 text-center" role="alert">
                  <p className="text-sm font-medium text-red-700">{t('suggestion.recentError')}</p>
                </div>
              )}

              {!isRecentLoading && !isRecentError && recentToShow.length === 0 && (
                <div className="p-6 text-center">
                  <p className="text-sm font-medium text-gray-900">{t('suggestion.noRecent')}</p>
                </div>
              )}

              {!isRecentLoading && recentToShow.length > 0 && (
                <div className="space-y-2">
                  {recentToShow.map((document, index) => (
                    <RecentDocumentDropDownItem
                      key={indexedItemKey(document)}
                      document={document}
                      type={type as 'favorite' | 'recent'}
                      index={index}
                      canShowDropdown={canShowDropdown}
                      setIsOpen={setIsOpen}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
